import { create } from 'zustand';
import { AIContext } from '../types';
import type { AnalysisResponse } from '../types/aiAnalysis';

export interface VisitRecording {
  id: string;
  patientId?: string;
  patientName?: string;
  date: string;
  duration: number;
  transcription: string;
  audio?: Blob;
  summary?: string;
  keyPoints?: string[];
  diagnoses?: string[];
  treatments?: string[];
  medications?: string[];
  followUp?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Lightweight context attachments displayed as chips above the AI
 * bubble's input field — Cursor / Copilot-style "what's loaded for
 * this conversation". The chip itself is the user-visible signal;
 * the actual context data flows server-side via the patient-bound
 * thread + semantic-context retrieval, NOT through these objects.
 *
 * Today only `patient` is supported. Encounter / lab / file chips
 * follow the same shape so the UI doesn't need to change when they
 * land.
 */
export interface AIContextChip {
  type: 'patient';
  /** Server entity id — used to bind the active chat thread. */
  id: string;
  /** Display label (patient name, encounter date, etc.). */
  label: string;
  /** Optional secondary line — e.g. "MRN A0F3E3" / "35y · Male". */
  meta?: string;
  /** Toggle: when false the chip is dimmed and DOES NOT contribute
   *  patient context to the next turn (Copilot-sidebar style "hide
   *  the page from this conversation"). Defaults to true; toggled by
   *  clicking the chip. */
  enabled?: boolean;
}

interface AIStore {
  contexts: AIContext[];
  showPanel: boolean;
  isListening: boolean;
  visitRecordings: VisitRecording[];
  activeRecording: VisitRecording | null;
  isRecordingVisit: boolean;
  showAssistant: boolean;
  pendingPrompt: string;
  /** A full chat turn to AUTO-SEND when the bubble opens (vs
   *  pendingPrompt, which only pre-fills the composer). Consumed once
   *  by the bubble, which starts a FRESH thread for it — producers are
   *  "chat about this X" entry points (e.g. the scan inbox) where
   *  appending to whatever thread the bubble last held would be
   *  confusing. */
  pendingChatMessage: string | null;
  pendingAnalysisRequest: unknown | null;
  /** Active context chips for the bubble's input area. Setting a
   *  chip here should also flip showAssistant=true so the bubble
   *  opens with the chip already in view. */
  contextChips: AIContextChip[];
  /** Workspace 'Voice mode' button → the bubble opens in continuous
   *  conversation mode (speak, listen, interrupt) and clears the flag. */
  pendingVoiceMode: boolean;

  // Actions
  addContext: (context: AIContext) => void;
  removeContext: (id: string) => void;
  clearAllContexts: () => void;
  togglePanel: () => void;
  setShowPanel: (show: boolean) => void;
  setIsListening: (listening: boolean) => void;
  setShowAssistant: (show: boolean) => void;
  sendAIPrompt: (prompt: string) => void;
  setPendingPrompt: (prompt: string) => void;
  /** Set to queue an auto-send (opens the bubble); the bubble clears
   *  it with null once consumed. */
  setPendingChatMessage: (message: string | null) => void;
  addAnalysisResult: (analysis: AnalysisResponse) => void;
  triggerAnalysis: (request: unknown) => void;
  /** Replace the active chip rail. Pass [] to clear. */
  setContextChips: (chips: AIContextChip[]) => void;
  /** Add or replace a chip by (type, id) identity. By default opens
   *  the bubble (user explicitly invoked "Add to context"); pass
   *  `{quiet:true}` for the auto-attach effect that runs on every
   *  patient page load — we don't want the bubble popping unsolicited. */
  setPendingVoiceMode: (pending: boolean) => void;
  attachContextChip: (chip: AIContextChip, options?: { quiet?: boolean }) => void;
  /** Remove a chip by id. */
  detachContextChip: (id: string) => void;
  /** Toggle a chip's enabled state. Disabled chips render dimmed and
   *  are skipped when the bubble resolves patient context for a turn —
   *  same as if the chip weren't there at all. */
  toggleContextChipEnabled: (id: string) => void;
  
  // Page context specific
  addPageContext: (view: string, pageData: unknown) => void;
  
  // Visit recording actions
  startVisitRecording: (patientId?: string, patientName?: string) => void;
  stopVisitRecording: () => void;
  updateActiveRecording: (updates: Partial<VisitRecording>) => void;
  saveVisitRecording: (recording: VisitRecording) => void;
  deleteVisitRecording: (id: string) => void;
  loadVisitRecording: (id: string) => void;
  updateVisitRecordingTranscription: (transcription: string) => void;
}

export const useAIStore = create<AIStore>((set, _get) => ({
  contexts: [],
  showPanel: false,
  isListening: false,
  visitRecordings: [],
  activeRecording: null,
  isRecordingVisit: false,
  showAssistant: false,
  pendingPrompt: '',
  pendingChatMessage: null,
  pendingAnalysisRequest: null,
  contextChips: [],
  pendingVoiceMode: false,
  
  addContext: (context) => set((state) => {
    // Check if context already exists
    const exists = state.contexts.some(ctx => ctx.id === context.id);
    if (exists) return state;
    
    return {
      contexts: [...state.contexts, context],
      showPanel: true // Auto open panel when adding context
    };
  }),
  
  removeContext: (id) => set((state) => ({
    contexts: state.contexts.filter(ctx => ctx.id !== id)
  })),
  
  clearAllContexts: () => set({ contexts: [] }),
  
  togglePanel: () => set((state) => ({ showPanel: !state.showPanel })),
  
  setShowPanel: (show) => set({ showPanel: show }),
  
  setIsListening: (listening) => set({ isListening: listening }),
  
  setShowAssistant: (show) => set({ showAssistant: show }),
  
  sendAIPrompt: (prompt) => set((state) => ({
    showAssistant: true,
    contexts: [...state.contexts, {
      id: `prompt-${Date.now()}`,
      type: 'prompt',
      data: { prompt, timestamp: new Date().toISOString() },
      label: prompt.substring(0, 50) + (prompt.length > 50 ? '...' : '')
    }]
  })),

  setPendingPrompt: (prompt) => set({ pendingPrompt: prompt }),

  setPendingChatMessage: (message) =>
    set(
      message
        ? { pendingChatMessage: message, showAssistant: true }
        : { pendingChatMessage: null },
    ),

  addAnalysisResult: (analysis) => set((state) => ({
    showAssistant: true,
    contexts: [...state.contexts, {
      id: `analysis-${Date.now()}`,
      type: 'analysis',
      data: analysis,
      label: `AI Analysis: ${analysis.analysisType.replace(/-/g, ' ')}`
    }]
  })),

  triggerAnalysis: (request) => set({
    pendingAnalysisRequest: request,
    showAssistant: true, // Show the assistant bubble
  }),

  setContextChips: (chips) => set({ contextChips: chips }),

  setPendingVoiceMode: (pending: boolean) => set({ pendingVoiceMode: pending }),

  attachContextChip: (chip, options) => set((state) => {
    const others = state.contextChips.filter(
      (c) => !(c.type === chip.type && c.id === chip.id),
    );
    const next: Partial<AIStore> = {
      contextChips: [...others, { enabled: true, ...chip }],
    };
    // Only flip showAssistant=true for explicit user-initiated attaches.
    // The auto-attach effect on patient page load passes quiet:true so
    // the bubble doesn't pop unsolicited on every navigation.
    if (!options?.quiet) {
      next.showAssistant = true;
    }
    return next as AIStore;
  }),

  detachContextChip: (id) => set((state) => ({
    contextChips: state.contextChips.filter((c) => c.id !== id),
  })),

  toggleContextChipEnabled: (id) => set((state) => ({
    contextChips: state.contextChips.map((c) =>
      c.id === id ? { ...c, enabled: c.enabled === false ? true : false } : c,
    ),
  })),

  addPageContext: (view, pageData) => set((state) => {
    const pageId = `page-${view}-${Date.now()}`;
    const pageLabel = `${view.charAt(0).toUpperCase() + view.slice(1)} Page`;
    
    // Check if this page context already exists
    const existingPageIndex = state.contexts.findIndex(
      ctx => ctx.type === 'page' && (ctx.data as { currentView?: string }).currentView === view
    );
    
    if (existingPageIndex !== -1) {
      // Update existing page context
      const updatedContexts = [...state.contexts];
      updatedContexts[existingPageIndex] = {
        id: pageId,
        type: 'page',
        data: pageData,
        label: pageLabel
      };
      return { contexts: updatedContexts };
    } else {
      // Add new page context
      return {
        contexts: [...state.contexts, {
          id: pageId,
          type: 'page',
          data: pageData,
          label: pageLabel
        }]
      };
    }
  }),
  
  // Visit recording actions
  startVisitRecording: (patientId, patientName) => set({
    isRecordingVisit: true,
    activeRecording: {
      id: `visit-${Date.now()}`,
      patientId,
      patientName,
      date: new Date().toISOString(),
      duration: 0,
      transcription: '',
      createdAt: new Date(),
      updatedAt: new Date()
    }
  }),
  
  stopVisitRecording: () => set((state) => {
    if (state.activeRecording) {
      return {
        isRecordingVisit: false,
        visitRecordings: [...state.visitRecordings, state.activeRecording],
        activeRecording: null
      };
    }
    return { isRecordingVisit: false };
  }),
  
  updateActiveRecording: (updates) => set((state) => ({
    activeRecording: state.activeRecording 
      ? { ...state.activeRecording, ...updates, updatedAt: new Date() }
      : null
  })),
  
  saveVisitRecording: (recording) => set((state) => ({
    visitRecordings: [...state.visitRecordings.filter(r => r.id !== recording.id), recording]
  })),
  
  deleteVisitRecording: (id) => set((state) => ({
    visitRecordings: state.visitRecordings.filter(r => r.id !== id)
  })),
  
  loadVisitRecording: (id) => set((state) => {
    const recording = state.visitRecordings.find(r => r.id === id);
    if (recording) {
      return {
        activeRecording: recording,
        contexts: [...state.contexts, {
          id: `visit-context-${id}`,
          type: 'visit',
          data: recording,
          label: `Visit Recording - ${recording.patientName || 'Unknown Patient'}`
        }]
      };
    }
    return state;
  }),
  
  updateVisitRecordingTranscription: (transcription) => set((state) => ({
    activeRecording: state.activeRecording 
      ? { ...state.activeRecording, transcription, updatedAt: new Date() }
      : null
  }))
}));